/**
 * usePanelLayout
 *
 * Manages the order, visibility, and width of the five meeting panels.
 * Layout is persisted to localStorage so it survives page refreshes.
 *
 * Panels:
 *   followUp       - Follow-up Questions
 *   suggestions    - AI Suggestions
 *   transcript     - Transcript
 *   insights       - Account Insights
 *   capabilities   - Tech Capabilities
 */
import { useState, useCallback } from "react";

const STORAGE_KEY = "meeting_panel_layout_v3";

const DEFAULT_ORDER = [
  "followUp",
  "suggestions",
  "engineering",
  "relevantCards",
  "transcript",
  "insights",
];

const DEFAULT_VISIBLE = {
  followUp: true,
  suggestions: true,
  engineering: true,
  relevantCards: false, // minimized by default (like transcript)
  transcript: false,
  insights: false,
};

function loadLayout() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function saveLayout(order, visible) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ order, visible }));
  } catch {
    // storage quota exceeded or private mode — silently ignore
  }
}

export function usePanelLayout() {
  const [layout, setLayout] = useState(() => {
    const saved = loadLayout();
    if (saved) {
      // Ensure any newly-added panel id that wasn't in the saved layout is included
      const order = DEFAULT_ORDER.filter(
        (id) => !saved.order || !saved.order.includes(id)
          ? true
          : saved.order.includes(id)
      );
      const mergedOrder = [
        ...(saved.order || DEFAULT_ORDER),
        ...DEFAULT_ORDER.filter((id) => !(saved.order || []).includes(id)),
      ];
      const mergedVisible = { ...DEFAULT_VISIBLE, ...(saved.visible || {}) };
      return { order: mergedOrder, visible: mergedVisible };
    }
    return { order: DEFAULT_ORDER, visible: DEFAULT_VISIBLE };
  });

  const reorder = useCallback((newOrder) => {
    setLayout((prev) => {
      const next = { ...prev, order: newOrder };
      saveLayout(next.order, next.visible);
      return next;
    });
  }, []);

  const toggleVisible = useCallback((panelId) => {
    setLayout((prev) => {
      const next = {
        ...prev,
        visible: { ...prev.visible, [panelId]: !prev.visible[panelId] },
      };
      saveLayout(next.order, next.visible);
      return next;
    });
  }, []);

  const setVisible = useCallback((panelId, value) => {
    setLayout((prev) => {
      const next = {
        ...prev,
        visible: { ...prev.visible, [panelId]: value },
      };
      saveLayout(next.order, next.visible);
      return next;
    });
  }, []);

  const resetLayout = useCallback(() => {
    const next = { order: DEFAULT_ORDER, visible: DEFAULT_VISIBLE };
    setLayout(next);
    saveLayout(next.order, next.visible);
  }, []);

  return {
    order: layout.order,
    visible: layout.visible,
    reorder,
    toggleVisible,
    setVisible,
    resetLayout,
  };
}
